import type { Availability, TimeRange } from '@/types/tournament.model';
import { timeToMinutes } from '@/utils/time';

/**
 * "Does this pair (or this club block) cover this date and time?"
 *
 * Used to live inside `MatchDateTimeEditModal`. Pulled out here when the shared grid needed the
 * same criterion: if each view computed it on its own, the modal and the grid could disagree
 * about the same match.
 *
 * Both functions return `null` when there's nothing to answer with (no availability loaded,
 * no date, no time). `null` isn't "can't": it's "unknown", and the UI shows no icon.
 */

/**
 * The shared scan: does any range declared for `date` contain `time`?
 *
 * `rangeCounts` is what separates the two public variants — the club-block one rejects ranges
 * that offer no court — so the date filter and the range walk live here only once.
 */
function fitsDeclaredRanges(
  entries: Availability[] | undefined,
  date: string,
  time: string,
  rangeCounts: (range: TimeRange) => boolean,
): boolean | null {
  if (!entries || entries.length === 0) return null;
  if (!date || !time) return null;

  const t = timeToMinutes(time);
  if (t < 0) return null;

  const sameDayEntries = entries.filter((d) => d.date === date);
  if (sameDayEntries.length === 0) return false;

  return sameDayEntries.some((entry) =>
    (entry.timeRange ?? []).some((r) => {
      if (!rangeCounts(r)) return false;
      const bounds = rangeBounds(r);
      return bounds !== null && t >= bounds.start && t < bounds.end;
    }),
  );
}

const MIDNIGHT_END = 24 * 60;

type RangeEnds = Pick<TimeRange, 'startTime' | 'endTime'>;

/**
 * A range's `[start, end)` in minutes, or `null` when either end doesn't parse.
 *
 * An `00:00` end is the next midnight, not minute 0 of the day: without this, a `22:00`–`00:00`
 * range ends before it starts and covers nothing. Same rule as the backend's `TeamAvailabilityFit`,
 * which answers `GET /tournaments/{id}/shared-grid/available-teams`: if one side changes, the other
 * has to as well, or "who can play" and the grid's warnings stop agreeing.
 */
function rangeBounds(r: RangeEnds): { start: number; end: number } | null {
  const start = timeToMinutes(r.startTime);
  const rawEnd = timeToMinutes(r.endTime);
  if (start < 0 || rawEnd < 0) return null;
  return { start, end: rawEnd === 0 ? MIDNIGHT_END : rawEnd };
}

/** `date` in `YYYY-MM-DD`, `time` in `HH:mm`. */
export function fitsAvailability(
  availability: Availability[] | undefined,
  date: string,
  time: string,
): boolean | null {
  return fitsDeclaredRanges(availability, date, time, () => true);
}

/**
 * Same as `fitsAvailability` but for the club's blocks: a slot with `courtsQuantity <= 0`
 * offers no court, so it doesn't count as available even if the time falls within it.
 */
export function fitsTournamentSchedule(
  schedule: Availability[] | undefined,
  date: string,
  time: string,
): boolean | null {
  return fitsDeclaredRanges(
    schedule,
    date,
    time,
    (r) => (r.courtsQuantity ?? 0) > 0,
  );
}

/**
 * The common window of two teams: covers a date and time only when both do. It is the
 * availability of a dependent match's side while its team is unknown, since either team of the
 * source match can end up there.
 *
 * Same contract as the backend's `TeamAvailabilityFit.intersect`:
 * - `undefined` when either team has no availability loaded: "unknown", so the UI shows no icon.
 * - Otherwise one entry per date either team declared, even when no range is left. A date with no
 *   ranges reads as "can't", which is the honest answer; an empty list would read as "unknown".
 *
 * Ranges are read with the same `rangeBounds` as `fitsDeclaredRanges` (an `00:00` end included),
 * so `fitsAvailability(intersectAvailability(a, b), d, t)` equals
 * `fitsAvailability(a, d, t) && fitsAvailability(b, d, t)`.
 */
export function intersectAvailability(
  a: Availability[] | undefined,
  b: Availability[] | undefined,
): Availability[] | undefined {
  if (!a || a.length === 0 || !b || b.length === 0) return undefined;

  const dates = [...new Set([...a, ...b].map((entry) => entry.date))].sort();
  return dates.map((date) => {
    const entriesA = a.filter((entry) => entry.date === date);
    const entriesB = b.filter((entry) => entry.date === date);
    const rangesA = entriesA.flatMap((entry) => entry.timeRange ?? []);
    const rangesB = entriesB.flatMap((entry) => entry.timeRange ?? []);
    const common = rangesA.flatMap((rangeA) =>
      rangesB.flatMap((rangeB) => {
        const overlap = overlapRange(rangeA, rangeB);
        return overlap ? [overlap] : [];
      }),
    );
    return { ...(entriesA[0] ?? entriesB[0]), timeRange: common };
  });
}

/** `[later start, earlier end)`, keeping the original strings; `null` when they don't overlap. */
function overlapRange(a: TimeRange, b: TimeRange): TimeRange | null {
  const boundsA = rangeBounds(a);
  const boundsB = rangeBounds(b);
  if (!boundsA || !boundsB) return null;
  if (Math.max(boundsA.start, boundsB.start) >= Math.min(boundsA.end, boundsB.end)) return null;

  return {
    ...a,
    startTime: boundsA.start >= boundsB.start ? a.startTime : b.startTime,
    endTime: boundsA.end <= boundsB.end ? a.endTime : b.endTime,
  };
}

/**
 * Overlapping or back-to-back ranges merged into continuous stretches, in start order, keeping
 * the original strings; ranges that don't parse are dropped.
 *
 * Read with `rangeBounds`, except that a range ending before it starts (`23:00`–`00:30`) is taken
 * to cross midnight. `rangeBounds` reads it as covering nothing, like the backend's fit, but a
 * stretch shows what the team picked, and keeping the end before the start would let the next
 * range open an overlapping stretch instead of joining this one.
 */
export function mergeTimeRanges(ranges: RangeEnds[]): RangeEnds[] {
  const merged: RangeEnds[] = [];
  let lastEnd = -1;
  ranges
    .flatMap((r) => {
      const bounds = rangeBounds(r);
      if (!bounds) return [];
      const end =
        bounds.end < bounds.start ? bounds.end + MIDNIGHT_END : bounds.end;
      return [{ r, start: bounds.start, end }];
    })
    .sort((a, b) => a.start - b.start)
    .forEach(({ r, start, end }) => {
      if (start > lastEnd) {
        merged.push({ startTime: r.startTime, endTime: r.endTime });
        lastEnd = end;
      } else if (end > lastEnd) {
        merged[merged.length - 1].endTime = r.endTime;
        lastEnd = end;
      }
    });
  return merged;
}
